import {
  Pressable,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LogoSVG from '~/credsystem/assets/svgs/marisa-logo.svg';
import EyeOpenSVG from '~/credsystem/assets/svgs/eye-open.svg';
import EyeClosedSVG from '~/credsystem/assets/svgs/eye-closed.svg';
import ProfileCircleSVG from '~/credsystem/assets/svgs/profile-circle.svg';
import { Text } from '~/components';
import { ActionButton, Button, Progress } from './components';
import InvoiceSVG from '~/credsystem/assets/svgs/invoice.svg';
import HelpSVG from '~/credsystem/assets/svgs/help.svg';
import ArrowLeftSVG from '~/assets/svgs/arrow-left.svg';
import Skeleton from '~/components/Skeleton';
import Loader from '~/credsystem/scenes/Dashboard/components/Loader';
import useToast from '~/hooks/useToast';
import usecredsystemDataStore from '~/credsystem/stores/credsystemData';
import {
  formatCurrency,
  getEndCardNumber,
  stringToCapitalCase,
} from '~/utils/string';
import { formatDueDate } from '~/utils/date';
import { PayInvoiceModal } from '~/credsystem/components/PayInvoiceModal';
import useAppNavigation from '~/hooks/useAppNavigation';
import { CredsystemLocalStorage } from '~/credsystem/storage/LocalStorage';
import { CREDSYSTEM_SHOW_INFO } from '~/credsystem/storage/constants';
import colors from '~/assets/themes/colors';

function showsInfo(info: string | number, showInfo: boolean) {
  if (showInfo) return info;

  return '••••';
}

const Dashboard = () => {
  const { navigate } = useAppNavigation();
  const { showError } = useToast();
  const { credsystemCardInfo, getCardInfo, getInvoices } =
    usecredsystemDataStore.getState();

  const [cardLoading, setCardLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [showPayInvoiceModal, setShowPayInvoiceModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<StatementType | null>(
    null
  );

  const cardInfo = useMemo(() => {
    return {
      holder: credsystemCardInfo?.holder
        ? stringToCapitalCase(credsystemCardInfo.holder.split(' ')[0]) ||
          'Indefinido'
        : 'Indefinido',
      availableValue: credsystemCardInfo?.limit?.global?.available || 0,
      stipulatedValue: credsystemCardInfo?.limit?.global?.stipulated || 0,
      dueDate: formatDueDate(credsystemCardInfo?.dueDate, true),
      endNumber: getEndCardNumber(credsystemCardInfo?.cardNumber),
      id: getEndCardNumber(credsystemCardInfo?.cardNumber),
      usedValue:
        credsystemCardInfo?.limit?.global?.stipulated &&
        credsystemCardInfo?.limit?.global?.available
          ? credsystemCardInfo.limit.global.stipulated -
            credsystemCardInfo.limit.global.available
          : 0,
    };
  }, [credsystemCardInfo]);

  const fetchCardData = useCallback(async () => {
    setCardLoading(true);
    try {
      await getCardInfo();
    } catch (error) {
      showError({
        title: 'Houve um problema ao buscar as informações do seu cartão.',
        visibilityTime: 8000,
      });
    } finally {
      setCardLoading(false);
    }
  }, [getCardInfo, showError]);

  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      await getInvoices();
      setCurrentInvoice(usecredsystemDataStore.getState().getCurrentInvoice());
    } catch (error) {
      showError({
        title: 'Houve um problema ao buscar as informações da sua fatura.',
        visibilityTime: 8000,
      });
    } finally {
      setInvoicesLoading(false);
    }
  }, [getInvoices, showError]);

  const getStoredShowInfo = useCallback(async () => {
    const value = await CredsystemLocalStorage.get(CREDSYSTEM_SHOW_INFO);
    setShowInfo(!!value);
  }, []);

  const setStoredShowInfo = useCallback(async (value: boolean) => {
    await CredsystemLocalStorage.set(CREDSYSTEM_SHOW_INFO, value.toString());
    setShowInfo(value);
  }, []);

  useEffect(() => {
    getStoredShowInfo();
  }, [getStoredShowInfo]);

  useEffect(() => {
    fetchCardData();
  }, [fetchCardData]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <SafeAreaView className="bg-light-100 h-full container max-w-5xl mx-auto">
      <Skeleton loader={<Loader />} loading={cardLoading || invoicesLoading}>
        <PayInvoiceModal
          visible={showPayInvoiceModal}
          invoiceData={{
            invoiceValue: currentInvoice?.currentBalance,
            dueDate: currentInvoice?.dueDate,
            invoiceId: currentInvoice?.statementNumber,
            status: currentInvoice?.status,
          }}
          onClose={() => setShowPayInvoiceModal(false)}
        />
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          className="bg-light-100 flex-1"
          bounces={false}
        >
          <View className="bg-primary-600 p-6 pb-14 rounded-b-3xl">
            <View className="flex-row items-center justify-between">
              <Pressable onPress={() => navigate('MarisaServicos_Home')}>
                <ArrowLeftSVG color={colors.light[100]} />
              </Pressable>

              <View className="ml-11">
                <LogoSVG />
              </View>

              <View className="flex-row items-center">
                <View className="mr-5">
                  <TouchableOpacity
                    onPress={() => setStoredShowInfo(!showInfo)}
                  >
                    {showInfo ? (
                      <EyeOpenSVG />
                    ) : (
                      <EyeClosedSVG className="mt-1" />
                    )}
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  onPress={() => navigate('Credsystem_Settings')}
                >
                  <ProfileCircleSVG />
                </TouchableOpacity>
              </View>
            </View>

            <View className="mt-6 mb-12 flex-row items-start justify-between">
              <View>
                <Text className="text-light-100 text-base">
                  Olá, {showsInfo(cardInfo.holder, showInfo)}
                </Text>
                <Text className="text-secondary-200 text-[12px] mt-[2px]">
                  Cartão Final {showsInfo(cardInfo.endNumber, showInfo)}
                </Text>
              </View>

              <Text className="text-secondary-200 text-sm">
                Venc. Fatura: {showsInfo(cardInfo.dueDate, showInfo)}
              </Text>
            </View>

            <View pointerEvents="none">
              <Progress value={cardInfo.usedValue / cardInfo.stipulatedValue} />

              <View className="items-center absolute self-center mt-12">
                <Text className="text-light-100 text-sm mb-3">
                  Sua fatura atual
                </Text>

                <View className="flex-row items-center">
                  <Text className="text-light-100 text-sm">R$ </Text>
                  <Text className="text-light-100 text-3xl font-bold">
                    {showsInfo(
                      formatCurrency(currentInvoice?.invoiceValue || 0),
                      showInfo
                    )}
                  </Text>
                </View>

                <Text className="text-light-100 text-sm mt-3">
                  Limite disponível:
                </Text>
                <Text className="text-light-100 text-sm">
                  R$ {showsInfo(cardInfo.availableValue, showInfo)}
                </Text>
              </View>
            </View>

            <View className="flex-row items-center justify-center">
              <Button
                onPress={() => {
                  if (currentInvoice)
                    return navigate('Credsystem_Invoices', {
                      currentInvoiceId: currentInvoice.statementId,
                    });

                  return showError({
                    title: 'Não há faturas disponíveis no momento.',
                  });
                }}
              >
                Ver fatura
              </Button>

              {!!currentInvoice?.hasBill && (
                <Button
                  variant="secondary"
                  onPress={() => setShowPayInvoiceModal(true)}
                >
                  Pagar fatura
                </Button>
              )}
            </View>

            <View className="absolute bottom-[-80px] w-full self-center justify-center flex-row">
              {!!currentInvoice && (
                <ActionButton
                  icon={InvoiceSVG}
                  onPress={() =>
                    navigate('Credsystem_Invoices', {
                      currentInvoiceId: currentInvoice.statementId,
                    })
                  }
                >
                  Minhas faturas
                </ActionButton>
              )}

              <ActionButton
                icon={HelpSVG}
                onPress={() => navigate('Credsystem_SAC')}
              >
                Me Ajuda
              </ActionButton>
            </View>
          </View>
        </ScrollView>
      </Skeleton>
    </SafeAreaView>
  );
};

export default Dashboard;
